const trapFocusHandlers = {};

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

class SideDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeSideDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeSideDrawer(event, summaryElement)
        : this.openSideDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        document.documentElement.classList.add("mobile-submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openSideDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.documentElement.classList.add(`overlay--open`);
  }

  closeSideDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.documentElement.classList.remove(`overlay--open`);
    document.documentElement.classList.remove("mobile-submenu-open");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeSideDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    document.documentElement.classList.remove("mobile-submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("side-drawer", SideDrawer);

class HeaderDrawer extends SideDrawer {
  constructor() {
    super();
  }

  openSideDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".moonbase-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.documentElement.classList.add(`overlay--open`);
  }

  closeSideDrawer(event, elementToFocus) {
    super.closeSideDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
  }

  // Override the onFocusOut method so it does nothing on the Mobile Nav
  onFocusOut(event) {}
}

customElements.define("header-drawer", HeaderDrawer);

// Drawer Component
class DrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.closeDrawerButton = this.querySelectorAll("[drawer-close-button]");
    this.closeDrawerButton.forEach((btn) => {
      btn.addEventListener("click", this.closeDrawer.bind(this));
    });

    this.variants = window.Moonbase?.productJson?.variants || [];
    this.allOptionNames = [];
  }

  connectedCallback() {
    this.initVariantListeners();
    this.initChainStyleListeners();
    this.initRingSizeListeners();
    this.setSelectedOptionsAndUpdateUI();
  }

  openDrawer(drawer) {
    this.classList.add("drawer--open");
    document.documentElement.classList.add("drawer--open");
  }

  closeDrawer(drawer) {
    this.classList.remove("drawer--open");
    document.documentElement.classList.remove("drawer--open");
    if(this.id == "chain") {
      this.querySelectorAll('.secondary').forEach(el => {
        el.classList.add('hidden');
      });
      this.querySelector('.content.primary').classList.remove('hidden');
      this.querySelector('.title.primary').classList.remove('hidden');
    }
  }

   getCurrentSelections() {
    const selections = {};
    document.querySelectorAll('variant-selects select').forEach(select => {
      const match = select.name.match(/options\[(.+?)\]/);
      if (match) {
        const name = match[1];
        selections[name] = select.value;
        if (!this.allOptionNames.includes(name)) {
          this.allOptionNames.push(name);
        }
      }
    });
    return selections;
  }

  setSelectedOptionsAndUpdateUI() {
    const currentSelections = this.getCurrentSelections();

    const lis = this.querySelectorAll('.product-options li');
    if (!lis.length) return;

    const optionName = lis[0].getAttribute('data-option-name');
    const selectedValue = currentSelections[optionName];

    const label = this.previousElementSibling?.querySelector('[data-selected-option]');
    if (label && selectedValue) label.innerHTML = selectedValue;

    lis.forEach(li => {
      const val = li.getAttribute('data-option-value');
      li.classList.toggle('selected', val === selectedValue);
    });

    this.updateAvailability(currentSelections);
  }

  updateAvailability(currentSelections) {
    const lis = this.querySelectorAll('.product-options li');
    if (!lis.length) return;

    const optionName = lis[0].getAttribute('data-option-name');

    lis.forEach(li => {
      const value = li.getAttribute('data-option-value');
      const testSelection = { ...currentSelections, [optionName]: value };
      const testOptions = this.allOptionNames.map(name => testSelection[name] || null);

      const isAvailable = this.variants.some(v =>
        v.available &&
        (!v.option1 || v.option1 === testOptions[0]) &&
        (!v.option2 || v.option2 === testOptions[1]) &&
        (!v.option3 || v.option3 === testOptions[2])
      );

      li.classList.toggle('disabled', !isAvailable);
    });
  }

  initVariantListeners() {
    this.querySelectorAll('.product-options li').forEach(li => {
      li.addEventListener('click', () => {
        if (li.classList.contains('disabled')) return;

        const optionName = li.getAttribute('data-option-name');
        const optionValue = li.getAttribute('data-option-value');
        const select = document.querySelector(`variant-selects select[name="options[${optionName}]"]`);
        if (!select) return;

        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        this.setSelectedOptionsAndUpdateUI();
      });
    });
  }

  initChainStyleListeners() {
    if (this.id !== "chain") return;

    this.querySelectorAll('.style-selector').forEach(el => {
      el.addEventListener('click', () => {
        const targetId = el.getAttribute('data-style');
        const primary = this.querySelector('.content.primary');
        const primaryTitle = this.querySelector('.title.primary');
        const secondary = this.querySelector(`.secondary[data-style="${targetId}"]`);
        const secondaryTitle = this.querySelector('.title.secondary');
        if (primary && secondary) {
          primary.classList.add('hidden');
          primaryTitle.classList.add('hidden');
          secondary.classList.remove('hidden');
          secondaryTitle.classList.remove('hidden');
        }
      });
    });

    this.querySelectorAll('.style-products li').forEach(el => {
      el.addEventListener('click', () => {
        const variantId = el.getAttribute('data-variant-id');
        const productTitle = el.getAttribute('data-title');

        const input = document.querySelector('#chain-variant-id');
        if (input) {
          input.value = variantId;
          input.setAttribute('data-title', productTitle);
        }

        this.querySelectorAll('.style-products li').forEach(li => li.classList.remove('selected'));
        el.classList.add('selected');

        const selectedStyle = el.closest('.content')?.getAttribute('data-style');
        const parentSelector = `.primary .style-selector[data-style="${selectedStyle}"]`;
        const parent = this.querySelector(parentSelector);
        parent?.classList.add('selected');
      });
    });
  }

  initRingSizeListeners() {
    const tabs = this.querySelectorAll(".ring-size-selector li");
    const contents = this.querySelectorAll(".ring-size-content li");

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => {
        tabs.forEach(t => t.classList.remove("selected"));
        contents.forEach(c => c.classList.remove("selected"));
        tab.classList.add("selected");
        contents[index]?.classList.add("selected");
      });
    });

    this.querySelectorAll('.ring-size .select').forEach(el => {
      el.addEventListener('click', () => {
        const optionName = el.getAttribute('data-option-name');
        const optionValue = el.getAttribute('data-option-value');
        const select = document.querySelector(`variant-selects select[name="options[${optionName}]"]`);
        if (!select) return;

        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        const label = this.previousElementSibling?.querySelector('[data-selected-option]');
        if (label) label.innerHTML = optionValue;

        this.setSelectedOptionsAndUpdateUI();

        this.classList.remove("drawer--open");
        document.documentElement.classList.remove("drawer--open");
      });
    });
  }
}

// Define the custom element
customElements.define("drawer-component", DrawerComponent);

// Button Component
class ButtonComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addClickListener();
  }

  addClickListener() {
    const button = this.querySelector("button");
    const drawerId = this.getAttribute("data-drawer");
    const drawer = document.getElementById(drawerId);

    if (drawer) {
      button.addEventListener("click", () => {
        drawer.openDrawer(drawer);
      });
    }
  }
}

// Define the custom element
customElements.define("drawer-button", ButtonComponent);
